<script setup>
  import { ref, onMounted, computed, nextTick } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataTable from '@/components/DataTable.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useStaffStore } from '@/stores/staffStore'
  import { useCompanyStore } from '@/stores/companyStore'
  import { useBranchStore } from '@/stores/branchStore'
  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const staffStore = useStaffStore()
  const companyStore = useCompanyStore()
  const branchStore = useBranchStore()

  const breadcrumbs = ref([])

  // data ///////////////////////////////////////////
  const searchText = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')

  // 事業所選択用
  const selectedBranchId = ref(null)

  // computed ///////////////////////////////////////
  const svgText = ref(null)
  const svgTextWidth = ref(0)
  const companyName = computed(() => {
    nextTick(() => {
      if (svgText.value) {
        svgTextWidth.value = svgText.value.getComputedTextLength()
      }
    })
    return companyStore.currentCompanyName
  })

  // フィルタリング用のcomputed
  const filteredStaff = computed(() => {
    // 事業所フィルター
    let filtered = [...staffStore.staff]
    // 事業所フィルター
    if (selectedBranchId.value !== null && selectedBranchId.value !== undefined && selectedBranchId.value !== '') {
      const branchName = branchStore.getBranchOptionLabel(selectedBranchId.value)

      if (branchName) {
        filtered = filtered.filter(staff => staff.branchName === branchName)
      }
    }
    // 検索テキスト
    if (searchText.value) {
      const text = searchText.value.toLowerCase()
      filtered = filtered.filter(staff => {
        return Object.keys(staff).some(key => {
          return String(staff[key]).toLowerCase().includes(text)
        })
      })
    }

    return filtered
  })

  // methods ////////////////////////////////////////
  onMounted(async () => {
    staffStore.staff = []
    // クエリのbranchIdを取得
    const queryBranchId = route.query.branchId
    selectedBranchId.value = queryBranchId ? queryBranchId : null
    const companyId = route.params.companyId

    // branchIdの有無に応じてパンくずリストを設定
    setupBreadcrumbs(selectedBranchId.value)

    if (companyId) {
      // データ取得
      await branchStore.getBranchOptions(companyId)

      // 選択されたbranchIdが存在するかどうかを確認
      if (selectedBranchId.value) {
        const branchExists = branchStore.branchOptions.some(branch => branch.value == selectedBranchId.value)

        // 存在しない場合は、nullに設定（すべての事業場を表示）
        if (!branchExists) {
          selectedBranchId.value = null
          // URL上のクエリパラメータも更新
          router.replace({
            query: { ...route.query, branchId: undefined },
          })
        }
      }

      if (!(await staffStore.getStaff(companyId))) {
        // データが取得できなかった場合は、前のページに戻る
        router.back()
        return
      }
    }

    // 選択中の会社情報がない、または異なる会社IDの場合
    if (companyId && (!companyStore.selectedCompany || companyStore.selectedCompany.id !== companyId)) {
      // APIから会社情報を取得
      await companyStore.getCompanyNameById(companyId)
    }

    staffStore.initTableList()
  })

  // パンくずリストを設定する関数
  const setupBreadcrumbs = branchId => {
    // 基本のパンくずリスト項目
    const baseBreadcrumbs = [
      { name: 'ダッシュボード', url: '/dashboard' },
      { name: '会社一覧', url: '/company' },
    ]

    // branchIdがある場合は、事業場一覧を含める
    if (branchId) {
      baseBreadcrumbs.push({
        name: '事業場一覧',
        path: { name: 'branch', params: { companyId: route.params.companyId } },
      })
    }

    // 最後に従業員一覧を追加
    baseBreadcrumbs.push({ name: '従業員一覧', url: '' })

    // breadcrumbsを更新
    breadcrumbs.value = baseBreadcrumbs
  }

  // 従業員編集画面に遷移
  const editStaff = item => {
    // 事業場IDを取得
    router.push({
      name: 'staffdetail',
      params: { companyId: route.params.companyId, staffId: item.id },
    })
  }

  // 新規従業員登録画面に遷移
  const createStaff = () => {
    // 現在選択されている事業場IDを取得
    const branchId = selectedBranchId.value

    // 従業員詳細画面（新規作成モード）に遷移
    router.push({
      name: 'staffdetail',
      params: {
        companyId: route.params.companyId,
        staffId: 'new',
      },
      query: branchId ? { branchId: branchId } : {},
    })
  }

  // 戻るボタン
  const goBack = () => {
    companyStore.currentCompanyName = ''

    // 現在選択されている事業場IDがある場合は事業場一覧に戻る
    if (selectedBranchId.value) {
      router.push({
        name: 'branch',
        params: { companyId: route.params.companyId },
      })
    } else {
      // 事業場IDがない場合は会社一覧に戻る
      router.push({ name: 'company' })
    }
  }

  // 事業場選択変更時のハンドラー
  const onBranchChange = () => {
    // URLのクエリパラメータを更新
    const query = { ...route.query }
    if (selectedBranchId.value && selectedBranchId.value !== null) {
      query.branchId = selectedBranchId.value
    } else {
      delete query.branchId
    }

    router.replace({
      name: route.name,
      params: route.params,
      query: query,
    })
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="goBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>
        <svg :width="svgTextWidth" height="31" xmlns="http://www.w3.org/2000/svg" :viewBox="`0 0 ${svgTextWidth} 31`">
          <text ref="svgText" x="0" y="24" font-size="27" fill="var(--active-color)">{{ companyName }} 従業員一覧</text>
        </svg>
      </h2>

      <button type="button" class="new" @click="createStaff">従業員追加</button>

      <div class="filters">
        <!-- 事業所選択ボックス -->
        <div class="select">
          事業場:
          <select v-model="selectedBranchId" @change="onBranchChange">
            <option :value="null">すべて</option>
            <option v-for="branch in branchStore.branchOptions" :key="branch.value" :value="branch.value">
              {{ branch.label }}
            </option>
          </select>
        </div>
        <!-- 検索ボックス -->
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>

        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>

    <div class="main-body">
      <DataTable :table-list="staffStore.tableList" :data-list="filteredStaff" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="editStaff" @length="length = $event"></DataTable>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
</template>

<style lang="less" scoped>
  .company-subtitle {
    font-size: 0.9rem;
    color: #666;
  }

  .material-symbols-outlined {
    vertical-align: middle;
  }
</style>
